import { useEffect, useRef, useState } from "react";
import { Location, useLocation } from "react-router-dom";

import { DropdownNavItem } from "@layouts/types";
import { ERoutes } from "@utils/constants";

import { useIsDesktop } from "./layout";

type GetSubitems = (location: Location, isDesktop: boolean) => DropdownNavItem[];

export const useSubitems = (getSubitems: GetSubitems) => {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const underlineRef = useRef<HTMLDivElement | null>(null);
  const pathnameRef = useRef(location.pathname);
  const [subitems, setSubitems] = useState<DropdownNavItem[]>([]);
  const [prevPath, setPrevPath] = useState("");
  const activeIndex = useRef(-1);

  const updateUnderline = () => {
    const currentPathname = pathnameRef.current;

    let activeI = subitems.findIndex(({ route }) => route === currentPathname);
    if (currentPathname === ERoutes.CatalogLandingFreeBackground || currentPathname.includes(ERoutes.Deals)) {
      activeI = 0;
    }
    const activeEl = itemRefs.current[activeI];

    if (activeEl && underlineRef.current) {
      if (!activeEl.offsetLeft && !activeEl.offsetWidth) {
        setTimeout(() => updateUnderline(), 100);
        return;
      }
      const { left } = activeEl.getBoundingClientRect();
      underlineRef.current.style.left = `${left}px`;
      underlineRef.current.style.width = `${activeEl.offsetWidth}px`;

      activeIndex.current = activeI;
    }
  };

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const newSubitems = getSubitems(location, isDesktop);

    const underline = underlineRef.current;

    if (underline && prevPath !== location.pathname) {
      underline.style.left = `0px`;
      underline.style.width = `0px`;
    }
    if (newSubitems) {
      setSubitems([...newSubitems]);
      setPrevPath(location.pathname);
    }
  }, [location, getSubitems, isDesktop]);

  useEffect(() => {
    updateUnderline();
    window.addEventListener("resize", updateUnderline);
    return () => {
      window.removeEventListener("resize", updateUnderline);
    };
  }, [location.pathname, subitems, isDesktop]);

  return {
    subitems,
    itemRefs,
    underlineRef,
    activeIndex,
    updateUnderline,
  };
};
